import { useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { deliverableRepository } from '../repositories/deliverable.repository';
import type { ProcessDeliverableUrlResult } from '../types/deliverable.types';
import { deliverableKeys } from '../services/deliverable.keys';
import { contentKeys } from '@/domains/content/services/content.keys';

export interface UseImportDeliverableUrlsReturn {
  isProcessing: boolean;
  currentIndex: number;
  totalCount: number;
  results: ProcessDeliverableUrlResult[];
  isComplete: boolean;
  processUrls: (urls: string[]) => Promise<void>;
  retryFailed: () => Promise<void>;
  reset: () => void;
  cancel: () => void;
}

export const useImportDeliverableUrls = (
  campaignId: string,
  orgId: string
): UseImportDeliverableUrlsReturn => {
  const queryClient = useQueryClient();
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [results, setResults] = useState<ProcessDeliverableUrlResult[]>([]);
  const [isComplete, setIsComplete] = useState(false);
  const isCancelledRef = useRef(false);

  const reset = useCallback(() => {
    setIsProcessing(false);
    setCurrentIndex(0);
    setTotalCount(0);
    setResults([]);
    setIsComplete(false);
    isCancelledRef.current = false;
  }, []);

  const cancel = useCallback(() => {
    isCancelledRef.current = true;
    setIsProcessing(false);
    setIsComplete(true);
  }, []);

  const invalidateCaches = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['deliverable-aggregates'] });
    queryClient.invalidateQueries({ queryKey: ['influencer-posts'] });
    queryClient.invalidateQueries({ queryKey: contentKeys.all });
    if (orgId) {
      queryClient.invalidateQueries({ queryKey: deliverableKeys.campaignAggregates(orgId, campaignId) });
      queryClient.invalidateQueries({ queryKey: deliverableKeys.legacyCampaignAggregates(campaignId) });
      queryClient.invalidateQueries({ queryKey: ['influencers', orgId] });
    }
    queryClient.invalidateQueries({ queryKey: ['campaign-influencers', campaignId] });
    queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId] });
  }, [campaignId, orgId, queryClient]);

  const processUrls = useCallback(
    async (urls: string[]) => {
      if (urls.length === 0) return;

      isCancelledRef.current = false;
      setIsProcessing(true);
      setIsComplete(false);
      setCurrentIndex(0);
      setTotalCount(urls.length);
      setResults([]);

      const accumulatedResults: ProcessDeliverableUrlResult[] = [];
      let anySucceeded = false;

      for (let i = 0; i < urls.length; i++) {
        if (isCancelledRef.current) break;

        setCurrentIndex(i + 1);
        const currentUrl = urls[i];

        try {
          const result = await deliverableRepository.importDeliverableUrl(campaignId, currentUrl);
          accumulatedResults.push(result);
          setResults([...accumulatedResults]);

          if (result.status === 'success') {
            anySucceeded = true;
          }
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : 'Unknown error occurred';
          accumulatedResults.push({
            status: 'failed',
            url: currentUrl,
            error: message,
          });
          setResults([...accumulatedResults]);
        }

        // Pacing delay to avoid API burst limits and ensure DB transactions settle
        if (i < urls.length - 1 && !isCancelledRef.current) {
          await new Promise((resolve) => setTimeout(resolve, 400));
        }
      }

      setIsProcessing(false);
      setIsComplete(true);

      // Invalidate relevant caches if any items were added or updated
      if (anySucceeded) {
        invalidateCaches();
      }
    },
    [campaignId, invalidateCaches]
  );

  const retryFailed = useCallback(async () => {
    const failedItems = results.filter((r) => r.status === 'failed');
    if (failedItems.length === 0) return;

    isCancelledRef.current = false;
    setIsProcessing(true);
    setIsComplete(false);
    setCurrentIndex(0);
    setTotalCount(failedItems.length);

    let anySucceeded = false;

    for (let i = 0; i < failedItems.length; i++) {
      if (isCancelledRef.current) break;

      setCurrentIndex(i + 1);
      const itemToRetry = failedItems[i];

      try {
        const result = await deliverableRepository.importDeliverableUrl(campaignId, itemToRetry.url);
        if (result.status === 'success') {
          anySucceeded = true;
        }

        setResults((prev) =>
          prev.map((r) => (r.url === itemToRetry.url ? result : r))
        );
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Unknown error occurred';
        const failedResult: ProcessDeliverableUrlResult = {
          status: 'failed',
          url: itemToRetry.url,
          error: message,
        };
        setResults((prev) =>
          prev.map((r) => (r.url === itemToRetry.url ? failedResult : r))
        );
      }

      if (i < failedItems.length - 1 && !isCancelledRef.current) {
        await new Promise((resolve) => setTimeout(resolve, 400));
      }
    }

    setIsProcessing(false);
    setIsComplete(true);

    if (anySucceeded) {
      invalidateCaches();
    }
  }, [campaignId, invalidateCaches, results]);

  return {
    isProcessing,
    currentIndex,
    totalCount,
    results,
    isComplete,
    processUrls,
    retryFailed,
    reset,
    cancel,
  };
};
